import React, { useCallback, useEffect, useState } from "react";
import { Star, Plus, Pencil, Trash2, Eye, EyeOff, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";

const Stars = ({ n }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`h-4 w-4 ${i <= n ? "fill-amber-400 text-amber-400" : "text-brand-ink/20"}`} />
    ))}
  </span>
);

const emptyForm = { id: null, name: "", rating: 5, text: "", sort: 0, visible: true };

// Dev: manage homepage reviews + the «Review us on Google» link
export const ReviewsPanel = () => {
  const { t } = useLang();
  const d = t.admin.dev;
  const [reviews, setReviews] = useState(null);
  const [googleUrl, setGoogleUrl] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/reviews").then((r) => {
      setReviews(r.data.reviews);
      setGoogleUrl(r.data.google_url || "");
    }).catch(() => toast.error("Error"));
  }, []);
  useEffect(load, [load]);

  const save = async () => {
    if (!form.name.trim() || !form.text.trim()) return;
    setBusy(true);
    try {
      const payload = { name: form.name.trim(), rating: Number(form.rating), text: form.text.trim(), sort: Number(form.sort) || 0, visible: form.visible };
      if (form.id) await api.put(`/admin/reviews/${form.id}`, payload);
      else await api.post("/admin/reviews", payload);
      toast.success(d.rev_saved);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  const toggleVisible = async (rev) => {
    try {
      await api.put(`/admin/reviews/${rev.id}`, { name: rev.name, rating: rev.rating, text: rev.text, sort: rev.sort || 0, visible: !rev.visible });
      load();
    } catch { toast.error("Error"); }
  };

  const del = async (rev) => {
    if (!window.confirm(d.rev_delete_confirm)) return;
    try {
      await api.delete(`/admin/reviews/${rev.id}`);
      toast.success(d.rev_deleted);
      if (form.id === rev.id) setForm(emptyForm);
      load();
    } catch { toast.error("Error"); }
  };

  const saveGoogle = async () => {
    setBusy(true);
    try {
      await api.put("/admin/reviews-settings", { google_url: googleUrl.trim() });
      toast.success(d.rev_google_saved);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  if (reviews === null) return null;

  return (
    <div className="rounded-2xl bg-white border border-brand/10 p-5 shadow-[0_8px_30px_rgba(134,77,99,0.06)]" data-testid="reviews-panel">
      <p className="font-heading text-lg font-bold text-brand-ink flex items-center gap-2"><Star className="h-5 w-5 text-brand" /> {d.rev_title}</p>
      <p className="text-xs text-brand-ink/50 mt-0.5">{d.rev_sub}</p>

      {/* Add / edit form */}
      <div className="mt-4 rounded-xl border border-brand/15 bg-stone-alt/40 p-4" data-testid="review-form">
        <p className="text-xs font-bold text-brand-ink/70">{form.id ? d.rev_edit : d.rev_add}</p>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">{d.rev_name}</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl border-brand/20 bg-white h-10 mt-1" data-testid="review-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{d.rev_rating}</Label>
              <Input type="number" min="1" max="5" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} className="rounded-xl border-brand/20 bg-white h-10 mt-1" data-testid="review-rating" />
            </div>
            <div>
              <Label className="text-xs">{d.rev_sort}</Label>
              <Input type="number" value={form.sort} onChange={(e) => setForm({ ...form, sort: e.target.value })} className="rounded-xl border-brand/20 bg-white h-10 mt-1" data-testid="review-sort" />
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">{d.rev_text}</Label>
            <Textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className="rounded-xl border-brand/20 bg-white min-h-[80px] mt-1" data-testid="review-text" />
          </div>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <Button onClick={save} disabled={busy || !form.name.trim() || !form.text.trim()} className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-9" data-testid="review-save">
            {form.id ? d.rev_save : <span className="inline-flex items-center gap-1.5"><Plus className="h-4 w-4" /> {d.rev_add}</span>}
          </Button>
          {form.id && (
            <button type="button" onClick={() => setForm(emptyForm)} className="text-xs font-bold text-brand-ink/50 hover:underline" data-testid="review-cancel-edit">✕</button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="mt-4 space-y-2" data-testid="reviews-admin-list">
        {reviews.length === 0 && <p className="text-sm text-brand-ink/40 italic py-4 text-center">{d.rev_empty}</p>}
        {reviews.map((rev) => (
          <div key={rev.id} className={`flex items-start gap-3 rounded-xl border px-3.5 py-3 ${rev.visible ? "border-brand/10 bg-white" : "border-brand/10 bg-stone-alt/40 opacity-60"}`} data-testid={`review-row-${rev.id}`}>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-brand-ink text-sm flex items-center gap-2">
                {rev.name} <Stars n={rev.rating} />
                {!rev.visible && <span className="text-[10px] font-bold uppercase text-brand-ink/40">({d.rev_hidden})</span>}
              </p>
              <p className="text-xs text-brand-ink/60 mt-0.5 whitespace-pre-wrap">{rev.text}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button type="button" onClick={() => toggleVisible(rev)} className="grid place-items-center h-8 w-8 rounded-full hover:bg-brand-soft/60 text-brand" data-testid={`review-visible-${rev.id}`} aria-label={rev.visible ? d.rev_hidden : d.rev_visible}>
                {rev.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              </button>
              <button type="button" onClick={() => setForm({ id: rev.id, name: rev.name, rating: rev.rating, text: rev.text, sort: rev.sort || 0, visible: rev.visible })} className="grid place-items-center h-8 w-8 rounded-full hover:bg-brand-soft/60 text-brand" data-testid={`review-edit-${rev.id}`} aria-label={d.rev_edit}>
                <Pencil className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => del(rev)} className="grid place-items-center h-8 w-8 rounded-full hover:bg-rose-50 text-rose-500" data-testid={`review-delete-${rev.id}`} aria-label="Delete">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Google review link */}
      <div className="mt-5 rounded-xl border border-brand/15 bg-stone-alt/40 p-4" data-testid="review-google-form">
        <Label className="text-xs font-bold text-brand-ink/70 flex items-center gap-1.5"><ExternalLink className="h-3.5 w-3.5 text-brand" /> {d.rev_google_url}</Label>
        <div className="mt-2 flex flex-col sm:flex-row gap-2">
          <Input value={googleUrl} onChange={(e) => setGoogleUrl(e.target.value)} placeholder="https://g.page/…" className="rounded-xl border-brand/20 bg-white h-10 flex-1" data-testid="review-google-url" />
          <Button onClick={saveGoogle} disabled={busy} className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-10 shrink-0" data-testid="review-google-save">{d.rev_google_save}</Button>
        </div>
        <p className="mt-1.5 text-[11px] text-brand-ink/40">{d.rev_google_hint}</p>
      </div>
    </div>
  );
};
